import * as React from 'react';
import {
  Form, FormField, FormItem, FormControl, FormMessage,
  Button, KeyValueInput
} from '@virlewood/board-79';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useOnboardingStore } from '../../stores/useOnboardingStore';
import { motion } from 'framer-motion';

const goalsSchema = z.object({
  goals: z.record(z.string(), z.number())
    .refine((val) => Object.keys(val).length > 0, {
      message: 'Aggiungi almeno un traguardo'
    })
});

type GoalsFormData = z.infer<typeof goalsSchema>;

// Category names for display
const CATEGORY_NAMES: Record<string, string> = {
  'acc4c9e7-2afd-42c7-8da4-64e39d343bcd': 'ristoranti e bar',
  'c526e085-271c-45c0-b5fb-db5ce5b796f1': 'bellezza e benessere',
  '665d3004-18ec-40a3-8ec4-c5068fab39b9': 'negozi alimentari',
  '0332c1b2-abe9-420f-9113-faef6b1f1e27': 'artigiani e servizi',
  '90542ffc-6fe2-4bd1-a8c1-6f581968a029': 'negozi',
  '56ffabe3-5b9b-4fa0-bd49-db5a42817ee0': '' // Altro - empty string for special handling
};

// Predefined goals for each business category (6 goals per category)
const CATEGORY_GOALS: Record<string, Record<string, number>> = {
  // Ristorazione
  '7b2e1ff5-f23a-43f9-82bc-20ac72f133bd': {
    'Caffè gratis': 20,
    'Dolce omaggio': 50,
    'Aperitivo offerto': 80,
    'Buono da 10€': 100,
    'Cena per due': 300,
  },
  // Parrucchiere
  '956ede8f-3646-4328-809e-fea199026b1c': {
    'Piega omaggio': 80,
    'Sconto 20%': 100,
    'Trattamento omaggio': 200,
    'Buono da 25€': 250,
    'Giornata benessere': 400,
  },
  // Alimentari
  'e4da7c0c-5eb3-42f7-ab82-eab79e72a915': {
    'Prodotto omaggio': 50,
    'Sconto 15%': 75,
    'Buono spesa 10€': 100,
    'Buono spesa 20€': 200,
    'Cesto regalo': 300,
  },
  // Artigianato
  'eec0c763-5b58-44ab-848c-260e057bcb82': {
    'Sconto 15%': 75,
    'Buono da 10€': 100,
    'Sconto 25%': 200,
    'Servizio gratuito': 300,
    'Buono da 50€': 500,
  },
  // Abbigliamento
  'cd35ca46-bd2d-4a54-98d0-760fdd6a873d': {
    'Accessorio gratis': 80,
    'Sconto 20%': 100,
    'Buono da 20€': 200,
    'Sconto 30%': 300,
    'Buono da 50€': 500,
  },
  // Altro
  '56ffabe3-5b9b-4fa0-bd49-db5a42817ee0': {
    'Sconto 15%': 75,
    'Buono da 10€': 100,
    'Sconto 25%': 200,
    'Buono da 25€': 250,
    'Regalo speciale': 400,
  }
};

const CustomizeGoals = () => {
  const { updateData, data, nextStep } = useOnboardingStore(state => state);

  // Get predefined goals for the selected category (all 6)
  const categoryGoals = data.business_category
    ? CATEGORY_GOALS[data.business_category] || {}
    : {};

  // Get category name for display
  const categoryName = data.business_category
    ? CATEGORY_NAMES[data.business_category]
    : '';

  // Build the goals label text
  const goalsLabel = categoryName
    ? `I traguardi più comuni tra i ${categoryName}`
    : 'I traguardi più comuni';

  const form = useForm<GoalsFormData>({
    resolver: zodResolver(goalsSchema),
    mode: 'onChange',
    defaultValues: {
      // Use saved goals if they exist, otherwise use all 6 category defaults
      goals: data.goals && Object.keys(data.goals).length > 0
        ? data.goals
        : categoryGoals
    }
  });

  const onSubmit = (formData: GoalsFormData) => {
    // Save form data to store on submit
    updateData({
      goals: formData.goals
    });

    // Proceed to next step (which will trigger submission)
    nextStep();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-xl mx-auto"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-12">

          {/* Goals Section */}
          <div>
            <h3 className="text-3xl text-balance font-semibold text-center">{goalsLabel}</h3>
            <p className={'text-center text-sm max-w-sm mx-auto text-balance mt-2 text-muted-foreground mb-10'}>
              Ti consigliamo questi traguardi per iniziare, potrai cambiarli quando vuoi.
            </p>
            <FormField
              control={form.control}
              name="goals"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <KeyValueInput
                      value={field.value}
                      onValueChange={field.onChange}
                      invalid={!!fieldState.error}
                      valueType="number"
                      min={1}
                      max={5000}
                      sortOrder="asc"
                      keyPlaceholder="Nome traguardo"
                      valuePlaceholder="Punti"

                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className={'bg-background sticky bottom-0 w-full px-4 pb-4 flex justify-center gap-4 z-55'}>
            <Button
              type={'submit'}
              variant="primary"
              size="lg"
              className="min-w-sm"
              disabled={!data.business_category}
            >
              Inizia la prova gratis
            </Button>
          </div>

        </form>
      </Form>
    </motion.div>
  );
};

export default CustomizeGoals;
